import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { showApiError } from "@/lib/error-toast";
import { useCurrentTenant } from "@/hooks/useCurrentTenant";
import styles from "./tenant-settings.module.css";

/**
 * F-065 — tenant-level opt-in for the read-only operator automation guide.
 *
 * The driver's ruling: an operator's total surface should be slim, and job
 * CONFIG must never be operator-visible — but a read-only list of what the
 * automations DO is a useful procedural guide in some shops and unwanted noise
 * in others. So it is a setting, OFF by default, not a hardcoded choice.
 *
 * Turning it on opens `/rules-guide` (prose only: name, description, one
 * sentence per rule). It never opens job configuration — the Job Types surface
 * stays gated on `manage-job-types` regardless.
 */
export function OperatorRulesSection() {
  const { data: tenant, loading } = useCurrentTenant();
  const [visible, setVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (!tenant) return;
    setVisible(tenant.operatorVisibleRules ?? false);
  }, [tenant]);

  const handleToggle = async (next: boolean) => {
    // Optimistic, reverted on failure — matches AutoExportSection.
    setVisible(next);
    setSaving(true);
    setMessage(null);
    try {
      await api.patch("/me/tenant", { operatorVisibleRules: next });
      setMessage({
        kind: "ok",
        text: next
          ? "Operators can now read the automation guide."
          : "The automation guide is admin-only again (default).",
      });
    } catch (e) {
      setVisible(!next);
      showApiError(e, { fallback: "Failed to save the automation-guide setting" });
      setMessage({ kind: "err", text: "Save failed — toggle reverted." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>Operator-visible automation rules</h2>
      <p className={styles.overviewHint}>
        Controls whether operators can open a read-only guide to this tenant's
        published automations — what each rule checks and what it does. Job
        configuration is never exposed by this setting: editing job types,
        forms and rules stays with administrators either way.
      </p>
      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--space-3, 0.75rem)",
          marginTop: "var(--space-4, 1rem)",
          cursor: loading || saving ? "wait" : "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={visible}
          disabled={loading || saving}
          onChange={(e) => void handleToggle(e.target.checked)}
          data-testid="tenant-operator-visible-rules-toggle"
          style={{ marginTop: "2px" }}
        />
        <span>
          <strong>Let operators read the automation guide</strong>
          <span className={styles.overviewHint} style={{ display: "block", marginTop: "2px" }}>
            {visible
              ? "Every signed-in operator can open the guide from the sidebar."
              : "Only administrators with Manage rules can open the guide (default)."}
          </span>
        </span>
      </label>
      {message && (
        <div
          className={message.kind === "ok" ? styles.testOk : styles.testErr}
          role={message.kind === "err" ? "alert" : "status"}
          style={{ marginTop: "var(--space-3, 0.75rem)" }}
        >
          {message.text}
        </div>
      )}
    </section>
  );
}
